import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/cart-context";
import { formatPrice } from "@/lib/format";
import { ShoppingBag, X } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

export function CartSummary() {
  const { items, subtotal, itemCount, removeItem, updateQuantity, clearCart } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (itemCount === 0) return null;

  return (
    <>
      {/* Floating cart button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-4 sm:bottom-8 sm:left-8 z-50 bg-ink text-white px-4 py-3 sm:px-6 sm:py-4 rounded-full shadow-[0_10px_30px_rgba(0,0,0,0.3)] font-sans font-bold text-[10px] sm:text-xs uppercase tracking-[0.1em] hover:-translate-y-1 transition-transform active:scale-[0.95] flex items-center gap-2 sm:gap-3"
      >
        <ShoppingBag className="w-4 h-4" />
        {itemCount} {itemCount === 1 ? "item" : "items"} — {formatPrice(subtotal)}
      </button>

      {/* Cart sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="bg-cream border-ink/10 w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="font-heading text-lg uppercase tracking-wider text-ink">
              Your Cart
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 flex flex-col h-[calc(100vh-180px)]">
            {/* Items */}
            <div className="flex-1 overflow-y-auto space-y-4">
              {items.map((item) => (
                <div key={item.itemId} className="bg-white border border-ink/10 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-heading text-sm font-extrabold uppercase tracking-wider text-ink">
                        {item.name}
                      </p>
                      <p className="font-serif-elegant text-ink/60 italic text-sm mt-1">
                        {formatPrice(item.unitPrice)} × {item.quantity}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-sm font-extrabold text-ink">
                        {formatPrice(item.unitPrice * item.quantity)}
                      </span>
                      <button
                        onClick={() => removeItem(item.itemId)}
                        className="text-ink/40 hover:text-ink transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Quantity controls */}
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() => updateQuantity(item.itemId, item.quantity - 1)}
                      className="w-7 h-7 border border-ink/20 flex items-center justify-center text-ink text-xs hover:bg-ink hover:text-white transition-colors"
                    >
                      −
                    </button>
                    <span className="font-heading text-sm font-extrabold w-6 text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.itemId, item.quantity + 1)}
                      className="w-7 h-7 border border-ink/20 flex items-center justify-center text-ink text-xs hover:bg-ink hover:text-white transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-ink/10 pt-4 mt-4 space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="font-heading text-xs uppercase tracking-wider text-ink/60">Subtotal</span>
                <span className="font-heading text-xl font-extrabold tracking-wider text-ink">
                  {formatPrice(subtotal)}
                </span>
              </div>
              <p className="font-serif-elegant text-xs italic text-ink/40">
                Service fees calculated at checkout.
              </p>
              <Button
                onClick={() => {
                  setOpen(false);
                  navigate("/checkout");
                }}
                className="w-full bg-ink text-white hover:bg-gold hover:text-ink font-heading text-xs uppercase tracking-wider py-6"
              >
                Proceed to Checkout
              </Button>
              <button
                onClick={clearCart}
                className="w-full text-center font-sans text-xs text-ink/40 hover:text-ink transition-colors"
              >
                Clear cart
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
